import { useState } from "react";
import { parseAsInteger, parseAsString, useQueryStates } from "nuqs";

import { Badge, Flex, Table, TableAction, Typography } from "@payping/web-components";
import { ColumnsType } from "@payping/web-components/components/Table/types";
import { toJalali } from "@payping/utils/dateUtils";
import { getCorrectDate } from "@/utils";

import useTranslator from "@/shared/translate/translator";
import { panSeparator } from "@/shared/utils";
import { useGetRefundList } from "@/refund/use-cases";
import { JALALI_DATE_TIME_FORMAT } from "@/shared/constants/date.constant";
import { RefundInfo } from "@/refund/repository/refund/v1/refund-v1-api";
import { REFUND_STATE_COLOR_DICTIONARY } from "@/refund/constants/dictionary.constant";
import RefundTransactionFilter from "@/refund/components/refund-transaction-filter";
import type { QueryStatesSetter, RefundFilterQueryParams } from "@/refund/domains/filter.domain";
import RefundTransactionDetails from "../refund-transaction-details";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

const RefundTransactions = () => {
  const { t: generalT } = useTranslator();
  const { t } = useTranslator("refund.transactions");

  const [queryParams, setQueryParams] = useQueryStates({
    pageNumber: parseAsInteger.withDefault(1),
    pageSize: parseAsInteger.withDefault(10),
    fromDate: parseAsString.withDefault(""),
    toDate: parseAsString.withDefault(""),
    states: parseAsInteger,
    paymentCodes: parseAsString.withDefault(""),
    minAmount: parseAsInteger,
    maxAmount: parseAsInteger,
    pan: parseAsString.withDefault(""),
  }) as [RefundFilterQueryParams, QueryStatesSetter<RefundFilterQueryParams>];

  const { data, isLoading } = useGetRefundList({
    PageNumber: queryParams.pageNumber,
    PageSize: queryParams.pageSize,
    PaymentCodes: queryParams.paymentCodes ? [queryParams.paymentCodes] : undefined,
    States: queryParams.states === null || queryParams.states === 0 ? undefined : [queryParams.states],
    CreationDateFrom: queryParams.fromDate || undefined,
    CreationDateTo: queryParams.toDate || undefined,
    MaxAmount: queryParams.maxAmount || undefined,
    MinAmount: queryParams.minAmount || undefined,
    Pan: queryParams.pan || undefined,
  });

  const [details, setDetails] = useState<RefundInfo | null>(null);
  const onDetailsClick = (record: RefundInfo) => () => {
    setDetails(record);
  };
  const closeDetails = () => {
    setDetails(null);
  };

  const columns: ColumnsType<RefundInfo> = [
    {
      dataIndex: "requestedAmount",
      render(value) {
        return (
          <Flex align="center">
            <Typography.Text>{formatLocalizedNumber({ value })}</Typography.Text>
            <CuToman width={24} height={24} />
          </Flex>
        );
      },
      title: t("table.amount"),
    },
    {
      dataIndex: "state",
      render(value: number, tableData) {
        return (
          <Flex gap="small">
            <Badge color={REFUND_STATE_COLOR_DICTIONARY[value.toString() ?? "0"]} />
            <span>{tableData.localizedState}</span>
          </Flex>
        );
      },
      title: t("table.status"),
    },
    {
      dataIndex: "creationDateTime",
      render(value) {
        return (
          <Flex justify="flex-start">
            <span style={{ direction: "ltr" }}>
              {toJalali({ date: getCorrectDate(value), toFormat: JALALI_DATE_TIME_FORMAT }).string || "-"}
            </span>
          </Flex>
        );
      },
      title: t("table.createdRefundDate"),
    },
    {
      dataIndex: "successDateTime",
      render(value) {
        return (
          <Flex justify="flex-start">
            <span style={{ direction: "ltr" }}>
              {toJalali({ date: getCorrectDate(value), toFormat: JALALI_DATE_TIME_FORMAT }).string || "-"}
            </span>
          </Flex>
        );
      },
      title: t("table.depositRefundDate"),
    },
    {
      dataIndex: "pan",
      render(value) {
        return panSeparator(value);
      },
      title: t("table.destination"),
    },
    {
      title: generalT("common.operations"),
      key: "action",
      render: (record) => (
        <Typography.Link onClick={onDetailsClick(record)} key={0}>
          <TableAction label={generalT("common.detail")} />
        </Typography.Link>
      ),
    },
  ];

  const pagination = {
    onChange: (pageNumber: number, pageSize: number): void => {
      setQueryParams((prev) => ({ ...prev, pageNumber, pageSize }));
    },
    pageNumber: queryParams.pageNumber,
    pageSize: queryParams.pageSize,
    total: data?.totalCount || 10,
  };

  return (
    <Flex vertical className="tw-w-full" gap={16}>
      <RefundTransactionFilter queryParams={queryParams} setQueryParams={setQueryParams} />
      <Table
        loading={isLoading}
        hasIdColumn
        size="large"
        columns={columns}
        pagination={pagination}
        dataSource={data?.data || []}
        rowKey={(row) => row.code}
      />
      <RefundTransactionDetails data={details} onCloseClicked={closeDetails} />
    </Flex>
  );
};

export default RefundTransactions;
